<script lang="ts">
    /**
     * PopPage - Pop 소개 (/pop)
     *
     * 🎯 JadEye(/jadeye)·Gridverse(/gridverse)·Pin(/pin)·Ink(/ink)와 **같은 층**이다 — 랜딩 SPA 안의 한 페이지라
     *    앱바·푸터가 저절로 공유된다. 게임(/pop/alchemist …)은 셸 밖의 화면이다(서비스 3층).
     *
     * 짜임은 InkPage 를 따른다: 머리 → 「무엇을 할까요?」 게임 카드 → 「Pop의 약속」.
     *
     * 🚨 게임을 여기에 손으로 적지 않는다 — 목록 · 문구는 전부 서버 주입값(window.JadEye.pop ← config/pop.php)이다.
     * 🚨 머리 제목 한 줄만은 여기 글자로 있다 — SeoTest 가 noscript 의 제목과 대조한다(서버 값은 pop.tagline).
     * 🚫 이 화면에는 광고를 그리지 않는다 — LandingApp 이 Pop 소개에서는 배너 컴포넌트를 만들지 않는다(「광고 없이」 가 약속이다).
     */
    import { getPop } from '../../../lib/pop';

    const pop = getPop();
    const games = pop?.games ?? [];

    let visible = $state(false);

    $effect(() => {
        const t = setTimeout(() => {
            visible = true;
        }, 100);
        return () => clearTimeout(t);
    });
</script>

<!-- title·description 은 config/seo.php 가 SSOT (LandingApp.applyMeta 참고) -->

<div class="pop-page">
    <section class="pop-hero">
        <div class="hero-inner" class:visible>
            <span class="hero-eyebrow">Pop</span>
            <h1 class="hero-title">짬 날 때 톡 하고 노는 곳</h1>
            <p class="hero-subtitle">
                {#each pop?.subtitle ?? [] as line, i}
                    {#if i > 0}<br />{/if}{line}
                {/each}
            </p>

            {#if games.length > 0}
                <div class="hero-actions">
                    <a href={games[0].path} class="hero-cta-primary">
                        {games[0].label} 하기
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                        </svg>
                    </a>
                </div>
            {/if}
        </div>
    </section>

    {#if games.length > 0}
        <section id="pop-games" class="games-section" aria-label="Pop 게임">
            <div class="games-inner">
                <h2 class="games-title">무엇을 할까요?</h2>
                <div class="games-grid">
                    {#each games as game (game.slug)}
                        <a class="game-card" href={game.path}>
                            <span class="game-icon" aria-hidden="true">{game.icon}</span>
                            <span class="game-body">
                                <span class="game-card-title">{game.label}</span>
                                <span class="game-card-desc">{game.summary}</span>
                            </span>
                            <svg class="game-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                            </svg>
                        </a>
                    {/each}
                </div>
            </div>
        </section>
    {/if}

    {#if pop && pop.promises.length > 0}
        <section class="concept-section">
            <div class="concept-inner" class:visible>
                <div class="concept-head">
                    <h2 class="concept-title">Pop의 약속</h2>
                </div>

                <div class="concept-grid">
                    {#each pop.promises as promise}
                        <div class="concept-card">
                            <span class="concept-emoji">{promise.icon}</span>
                            <h3 class="concept-card-title">{promise.title}</h3>
                            <p class="concept-card-desc">{promise.desc}</p>
                        </div>
                    {/each}
                </div>
            </div>
        </section>
    {/if}
</div>

<style>
    /* Pop 의 색 — 톡 터지는 산딸기빛 #C53A6B(카드 · 게임 화면과 같은 값) */
    .pop-page {
        width: 100%;
        --pop: #c53a6b;
        --pop-rgb: 197, 58, 107;
    }

    .pop-hero {
        min-height: 62vh;
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 7rem 1.5rem 3rem;
        background: linear-gradient(135deg, #fff4ec 0%, #fbeef6 35%, #fafbfc 100%);
        position: relative;
        overflow: hidden;
    }

    .pop-hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -30%;
        width: 80%;
        height: 80%;
        background: radial-gradient(circle, rgba(var(--pop-rgb), 0.08) 0%, transparent 70%);
        pointer-events: none;
    }

    .hero-inner {
        max-width: 48rem;
        margin: 0 auto;
        width: 100%;
        text-align: center;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .hero-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .hero-eyebrow {
        display: inline-block;
        font-size: 0.9rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: var(--pop);
        background: rgba(var(--pop-rgb), 0.1);
        padding: 0.4rem 1rem;
        border-radius: 999px;
        margin-bottom: 1.5rem;
    }

    .hero-title {
        font-size: clamp(2.2rem, 5vw, 3.5rem);
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.03em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 1.5rem;
    }

    .hero-subtitle {
        font-size: 1.1rem;
        color: var(--landing-text-sub, #666666);
        line-height: 1.8;
        margin: 0 0 2.25rem;
    }

    .hero-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 0.75rem;
        justify-content: center;
        align-items: center;
    }

    .hero-cta-primary {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.9rem 1.8rem;
        background: var(--pop);
        color: #ffffff;
        font-size: 1rem;
        font-weight: 600;
        text-decoration: none;
        border-radius: 999px;
        box-shadow: 0 8px 24px rgba(var(--pop-rgb), 0.25);
        transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
    }

    .hero-cta-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 12px 32px rgba(var(--pop-rgb), 0.32);
    }



    .games-section {
        padding: 3.5rem 1.5rem 0;
        background: var(--landing-bg, #ffffff);
    }

    .games-inner {
        max-width: 60rem;
        margin: 0 auto;
    }

    .games-title {
        font-size: clamp(1.4rem, 3vw, 1.8rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        text-align: center;
        margin: 0 0 1.75rem;
    }

    .games-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .game-card {
        display: flex;
        align-items: center;
        gap: 1.1rem;
        padding: 1.5rem 1.75rem;
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(var(--pop-rgb), 0.18);
        border-radius: 18px;
        text-decoration: none;
        transition:
            transform 0.25s cubic-bezier(0.16, 1, 0.3, 1),
            border-color 0.2s ease,
            box-shadow 0.25s ease;
    }

    .game-card:hover {
        transform: translateY(-3px);
        border-color: rgba(var(--pop-rgb), 0.45);
        box-shadow: 0 10px 26px rgba(var(--pop-rgb), 0.12);
    }

    .game-icon {
        flex-shrink: 0;
        font-size: 2.2rem;
        line-height: 1;
    }

    .game-body {
        display: flex;
        flex-direction: column;
        gap: 0.3rem;
        min-width: 0;
    }

    .game-card-title {
        font-size: 1.12rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
    }

    .game-card-desc {
        font-size: 0.9rem;
        line-height: 1.55;
        color: var(--landing-text-sub, #666666);
    }

    .game-arrow {
        margin-left: auto;
        flex-shrink: 0;
        color: var(--pop);
    }

    .concept-section {
        padding: 5rem 1.5rem;
        background: var(--landing-bg, #ffffff);
    }



    .concept-inner {
        max-width: 72rem;
        margin: 0 auto;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .concept-inner.visible {
        opacity: 1;
        transform: translateY(0);
    }

    .concept-head {
        text-align: center;
        margin-bottom: 3rem;
    }

    .concept-title {
        font-size: clamp(1.8rem, 4vw, 2.5rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.75rem;
    }


    .concept-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1.5rem;
        max-width: 60rem;
        margin: 0 auto;
    }

    .concept-card {
        background: var(--landing-bg-alt, #f8f9fa);
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 20px;
        padding: 2rem;
        text-align: center;
        transition:
            transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
            box-shadow 0.3s ease;
    }

    .concept-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(0, 0, 0, 0.07);
    }

    .concept-emoji {
        display: inline-block;
        font-size: 2.5rem;
        line-height: 1;
        margin-bottom: 1rem;
    }

    .concept-card-title {
        font-size: 1.2rem;
        font-weight: 700;
        color: var(--landing-text, #1a1a1a);
        margin: 0 0 0.6rem;
    }

    .concept-card-desc {
        font-size: 0.95rem;
        line-height: 1.7;
        color: var(--landing-text-sub, #666666);
        margin: 0;
    }




    @media (min-width: 768px) {
        .pop-hero {
            padding: 7rem 2.5rem 3rem;
        }

        .games-section {
            padding: 4.5rem 2.5rem 0;
        }

        .games-grid {
            grid-template-columns: repeat(2, 1fr);
        }

        .concept-section {
            padding: 6rem 2.5rem;
        }

        .concept-grid {
            grid-template-columns: repeat(3, 1fr);
        }
    }

    @media (min-width: 1024px) {
        .pop-hero {
            padding: 7rem 3rem 3rem;
        }

        .concept-section {
            padding: 7rem 3rem;
        }
    }
</style>
